/**
 * clientSettings — Pure client settings state (volume + keybinds entry) (SPY-213).
 */

export const DEFAULT_CLIENT_SETTINGS = Object.freeze({
  masterVolume: 1,
  sfxVolume: 1,
  musicVolume: 1
});

export const SETTINGS_ENTRIES = Object.freeze([
  Object.freeze({
    id: 'volume',
    label: 'Volume',
    kind: 'volume'
  }),
  Object.freeze({
    id: 'keybinds',
    label: 'Keybinds',
    kind: 'keybinds'
  })
]);

/**
 * @param {number} value
 * @returns {number}
 */
export function clampVolume(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * @param {object} [snapshot]
 * @returns {{ id: string, label: string, kind: string }[]}
 */
export function listSettingsEntries(snapshot = DEFAULT_CLIENT_SETTINGS) {
  return SETTINGS_ENTRIES.map((entry) => {
    if (entry.id === 'volume') {
      return {
        ...entry,
        masterVolume: clampVolume(snapshot?.masterVolume),
        sfxVolume: clampVolume(snapshot?.sfxVolume),
        musicVolume: clampVolume(snapshot?.musicVolume)
      };
    }
    return { ...entry };
  });
}

/**
 * @param {Partial<typeof DEFAULT_CLIENT_SETTINGS>} [initial]
 */
export function createClientSettings(initial = {}) {
  const state = {
    masterVolume: clampVolume(
      initial.masterVolume ?? DEFAULT_CLIENT_SETTINGS.masterVolume
    ),
    sfxVolume: clampVolume(
      initial.sfxVolume ?? DEFAULT_CLIENT_SETTINGS.sfxVolume
    ),
    musicVolume: clampVolume(
      initial.musicVolume ?? DEFAULT_CLIENT_SETTINGS.musicVolume
    )
  };

  function get() {
    return { ...state };
  }

  function getVolume(channel) {
    if (channel === 'sfx') return state.sfxVolume;
    if (channel === 'music') return state.musicVolume;
    return state.masterVolume;
  }

  function setVolume(channel, value) {
    const next = clampVolume(value);
    if (channel === 'sfx') state.sfxVolume = next;
    else if (channel === 'music') state.musicVolume = next;
    else state.masterVolume = next;
    return getVolume(channel);
  }

  return { get, getVolume, setVolume };
}
